import React from "react";
import { Alert, Stack } from "@mui/material";

import { LabelField, NotesField, SessionSelect } from "./MarkerFields";
import type { MarkerFieldsProps } from "./MarkerDialog.types";

/** Session select label, from the Figma spec. */
const SESSION_LABEL = "Apply phase line after the following session";

/**
 * Fields for a phase-line marker: the session the new phase starts after, a
 * label describing the change, and optional notes.
 *
 * Unlike an external event, a phase line is always anchored to one of this
 * target's sessions — it marks a change in its own teaching procedures — so it
 * offers neither a specific date nor applying the marker to other targets.
 *
 * @param props
 * @param props.values        - Current form values
 * @param props.sessions      - Sessions available to anchor the marker to
 * @param props.onFieldChange - Field setter owned by the dialog shell
 * @param [props.labelInputRef] - Focus target after a save
 */
const PhaseLineFields: React.FC<MarkerFieldsProps> = ({
  values,
  sessions,
  onFieldChange,
  labelInputRef,
}) => (
  <Stack gap={7}>
    <SessionSelect
      label={SESSION_LABEL}
      value={values.sessionId}
      sessions={sessions}
      onChange={(sessionId) => onFieldChange("sessionId", sessionId)}
    />

    <LabelField
      value={values.label}
      placeholder={'e.g., "Intervention · Array of 6 · Most-to-Least Prompting"'}
      helperText="Describe the change in conditions or teaching procedures at this session"
      inputRef={labelInputRef}
      onChange={(label) => onFieldChange("label", label)}
    />

    <NotesField value={values.notes} onChange={(notes) => onFieldChange("notes", notes)} />

    {/* Guidance: a phase line is an annotation, not a clinical phase change. */}
    <Alert severity="info" sx={{ borderRadius: 2 }}>
      Phase lines annotate the graph only. To change a target&apos;s clinical phase, edit the target
      or use its progression criteria.
    </Alert>
  </Stack>
);

export default PhaseLineFields;
